import React, { FC, ReactElement } from 'react';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import { nanoid } from 'nanoid';
import { CircularProgress } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import { CartItem as CartItemInterface, PricingInfo } from '../types';
import { displayAsPrice } from '../helpers';
import {
  BottomRowCell,
  DefaultTableCell,
  EmptyCell,
  RowTitleCell,
  TotalTableCell,
  useStyles,
} from './styles';
import ProductQuantityField from './ProductQuantityField';

interface Props {
  product: CartItemInterface;
  isCompact: boolean;
  loading: boolean;
  updateCartPrice(quantity: number): void;
  showCartError(error: string): void;
}

const ProductPriceTable: FC<Props> = ({
  product,
  loading,
  isCompact,
  updateCartPrice,
  showCartError,
}: Props): ReactElement => {
  const classes = useStyles({ isCompact });

  return (
    <Table aria-label="simple table">
      <TableHead>
        <TableRow>
          <EmptyCell />
          <DefaultTableCell>Prix</DefaultTableCell>
          <DefaultTableCell>Nb</DefaultTableCell>
          <TotalTableCell>Total</TotalTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {product.default && (
          <TableRow>
            <RowTitleCell className={classes.borderTop}>Standard</RowTitleCell>
            <DefaultTableCell aria-label="default-price">
              {displayAsPrice(product.default.price)}
            </DefaultTableCell>
            <DefaultTableCell aria-label="default-quantity">
              {product.default.quantity}
            </DefaultTableCell>
            <TotalTableCell aria-label="default-total">
              {displayAsPrice(product.default.price * product.default.quantity)}
            </TotalTableCell>
          </TableRow>
        )}
        {product.subsidies.map((pricingInfo: PricingInfo, index: number) => (
          <TableRow className={classes.subsidyRow} key={nanoid()}>
            <RowTitleCell
              className={classes.borderTop}
              aria-label={`subsidy-title-${index + 1}`}
            >{`Subvention${product.subsidies.length > 1 ? ` ${index + 1}` : ''}`}</RowTitleCell>
            <DefaultTableCell aria-label={`subsidy-price-${index + 1}`}>
              {displayAsPrice(pricingInfo.price)}
            </DefaultTableCell>
            <DefaultTableCell aria-label={`subsidy-quantity-${index + 1}`}>
              {pricingInfo.quantity}
            </DefaultTableCell>
            <TotalTableCell aria-label={`subsidy-total-${index + 1}`}>
              {displayAsPrice(pricingInfo.price * pricingInfo.quantity)}
            </TotalTableCell>
          </TableRow>
        ))}
        {!isCompact && (
          <TableRow>
            <EmptyCell />
            <EmptyCell />
            <BottomRowCell>
              <ProductQuantityField
                product={product}
                updateCartPrice={updateCartPrice}
                showCartError={showCartError}
              />
            </BottomRowCell>
            <BottomRowCell aria-label="item-total" className={classes.totalPrice}>
              {!loading ? displayAsPrice(product.total.price) : <CircularProgress />}
            </BottomRowCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default ProductPriceTable;
